import type { EFFECT_SIZE_CALCULATOR_STRINGS } from "@/components/tools/effect-size-calculator";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput, parseNumberInput } from "@/lib/parse-number";
import {
  cohenMagnitude,
  cohensF,
  dFromIndependentT,
  dFromPairedT,
  dToOddsRatio,
  dToR,
  type EffectFamily,
  etaSquaredFromSums,
  hedgesCorrection,
  independentEffect,
  oddsRatioToD,
  pairedEffect,
  partialEtaSquaredFromF,
  rToD,
} from "@/lib/tool-kit/stats-descriptive/effect-size";

/*
 * One function per mode of the effect-size widget: read that mode's fields, compute,
 * and describe the result as cards plus working lines. Every word comes from the
 * widget's strings; this module only decides which numbers go where.
 */

type Strings = typeof EFFECT_SIZE_CALCULATOR_STRINGS;
type Num = (value: number) => string;

export type FieldKey =
  | "m1"
  | "sd1"
  | "n1"
  | "m2"
  | "sd2"
  | "n2"
  | "pm1"
  | "psd1"
  | "pm2"
  | "psd2"
  | "sdDiff"
  | "t"
  | "tn1"
  | "tn2"
  | "tn"
  | "f"
  | "df1"
  | "df2"
  | "ssEffect"
  | "ssError"
  | "ssTotal"
  | "value";

export type Fields = Record<FieldKey, string>;

export interface EffectView {
  cards: { label: string; value: string; emphasis?: boolean }[];
  working: string[];
  /** The headline value and its family, for Cohen's benchmark sentence. */
  headline: { value: number; family: EffectFamily } | null;
}

function positive(raw: string): number | null {
  const value = parseNumberInput(raw);
  return value !== null && value > 0 ? value : null;
}

function count(raw: string, minimum = 2): number | null {
  const value = parseIntegerInput(raw);
  return value !== null && value >= minimum ? value : null;
}

function all<T>(values: (T | null)[]): values is T[] {
  return values.every((value) => value !== null);
}

export function independentView(
  fields: Fields,
  control: 1 | 2,
  strings: Strings,
  num: Num
): EffectView | null {
  const inputs = [
    parseNumberInput(fields.m1),
    positive(fields.sd1),
    count(fields.n1),
    parseNumberInput(fields.m2),
    positive(fields.sd2),
    count(fields.n2),
  ];
  if (!all(inputs)) {
    return null;
  }
  const [m1, sd1, n1, m2, sd2, n2] = inputs;
  const e = independentEffect(m1, sd1, n1, m2, sd2, n2, control);
  const sdControl = control === 1 ? sd1 : sd2;
  return {
    cards: [
      { label: strings.cohensD, value: num(e.d), emphasis: true },
      { label: strings.hedgesG, value: num(e.g) },
      { label: strings.glassDelta, value: num(e.glassDelta) },
      { label: strings.pooledSd, value: num(e.pooledSd) },
      { label: strings.meanDifference, value: num(e.meanDifference) },
      { label: strings.correction, value: num(e.correction) },
    ],
    working: [
      t(strings.pooledLine, {
        n1m: num(n1 - 1),
        n2m: num(n2 - 1),
        v1: num(sd1 * sd1),
        v2: num(sd2 * sd2),
        df: num(n1 + n2 - 2),
        sp: num(e.pooledSd),
      }),
      t(strings.dLine, {
        m1: num(m1),
        m2: num(m2),
        sp: num(e.pooledSd),
        d: num(e.d),
      }),
      t(strings.gLine, {
        nn: num(n1 + n2),
        j: num(e.correction),
        d: num(e.d),
        g: num(e.g),
      }),
      t(strings.glassLine, {
        m1: num(m1),
        m2: num(m2),
        sd: num(sdControl),
        delta: num(e.glassDelta),
      }),
    ],
    headline: { value: e.d, family: "d" },
  };
}

export function pairedView(
  fields: Fields,
  strings: Strings,
  num: Num
): EffectView | null {
  const inputs = [
    parseNumberInput(fields.pm1),
    positive(fields.psd1),
    parseNumberInput(fields.pm2),
    positive(fields.psd2),
  ];
  if (!all(inputs)) {
    return null;
  }
  const [m1, sd1, m2, sd2] = inputs;
  const sdDiff = positive(fields.sdDiff);
  const e = pairedEffect(m1, sd1, m2, sd2, sdDiff);
  const view: EffectView = {
    cards: [
      { label: strings.dAv, value: num(e.dAv), emphasis: e.dZ === null },
      { label: strings.meanDifference, value: num(e.meanDifference) },
      { label: strings.averageSd, value: num(e.averageSd) },
    ],
    working: [
      t(strings.dAvLine, {
        m1: num(m1),
        m2: num(m2),
        sd1: num(sd1),
        sd2: num(sd2),
        d: num(e.dAv),
      }),
    ],
    headline: { value: e.dAv, family: "d" },
  };
  if (e.dZ !== null && sdDiff !== null) {
    view.cards.unshift({ label: strings.dZ, value: num(e.dZ), emphasis: true });
    view.working.unshift(
      t(strings.dZLine, {
        diff: num(e.meanDifference),
        sd: num(sdDiff),
        d: num(e.dZ),
      })
    );
    view.headline = { value: e.dZ, family: "d" };
  } else {
    view.working.push(strings.dZMissing);
  }
  return view;
}

export function fromTView(
  fields: Fields,
  design: "independent" | "paired",
  strings: Strings,
  num: Num
): EffectView | null {
  const tValue = parseNumberInput(fields.t);
  if (tValue === null) {
    return null;
  }
  if (design === "paired") {
    const n = count(fields.tn);
    if (n === null) {
      return null;
    }
    const d = dFromPairedT(tValue, n);
    return {
      cards: [{ label: strings.dZ, value: num(d), emphasis: true }],
      working: [
        t(strings.tPairedLine, { t: num(tValue), n: num(n), d: num(d) }),
      ],
      headline: { value: d, family: "d" },
    };
  }
  const n1 = count(fields.tn1);
  const n2 = count(fields.tn2);
  if (n1 === null || n2 === null) {
    return null;
  }
  const d = dFromIndependentT(tValue, n1, n2);
  const j = hedgesCorrection(n1 + n2 - 2);
  return {
    cards: [
      { label: strings.cohensD, value: num(d), emphasis: true },
      { label: strings.hedgesG, value: num(j * d) },
    ],
    working: [
      t(strings.tIndependentLine, {
        t: num(tValue),
        n1: num(n1),
        n2: num(n2),
        d: num(d),
      }),
      t(strings.gLine, {
        nn: num(n1 + n2),
        j: num(j),
        d: num(d),
        g: num(j * d),
      }),
    ],
    headline: { value: d, family: "d" },
  };
}

export function anovaView(
  fields: Fields,
  source: "f" | "ss",
  strings: Strings,
  num: Num
): EffectView | null {
  if (source === "f") {
    const f = parseNumberInput(fields.f);
    const df1 = count(fields.df1, 1);
    const df2 = count(fields.df2, 1);
    if (f === null || f < 0 || df1 === null || df2 === null) {
      return null;
    }
    const partial = partialEtaSquaredFromF(f, df1, df2);
    return {
      cards: [
        { label: strings.partialEta, value: num(partial), emphasis: true },
        { label: strings.cohensF, value: num(cohensF(partial)) },
      ],
      working: [
        t(strings.partialFromFLine, {
          f: num(f),
          df1: num(df1),
          df2: num(df2),
          eta: num(partial),
        }),
        t(strings.fLine, { eta: num(partial), f: num(cohensF(partial)) }),
        strings.oneWayNote,
      ],
      headline: { value: partial, family: "eta" },
    };
  }
  const ssEffect = parseNumberInput(fields.ssEffect);
  const ssError = positive(fields.ssError);
  const ssTotal = positive(fields.ssTotal);
  if (
    ssEffect === null ||
    ssEffect < 0 ||
    ssError === null ||
    ssTotal === null
  ) {
    return null;
  }
  if (ssEffect + ssError > ssTotal * (1 + 1e-9)) {
    return null;
  }
  const e = etaSquaredFromSums(ssEffect, ssError, ssTotal);
  return {
    cards: [
      { label: strings.eta, value: num(e.etaSquared), emphasis: true },
      { label: strings.partialEta, value: num(e.partialEtaSquared) },
      { label: strings.cohensF, value: num(cohensF(e.etaSquared)) },
    ],
    working: [
      t(strings.etaLine, {
        effect: num(ssEffect),
        total: num(ssTotal),
        eta: num(e.etaSquared),
      }),
      t(strings.partialLine, {
        effect: num(ssEffect),
        error: num(ssError),
        eta: num(e.partialEtaSquared),
      }),
      t(strings.fLine, {
        eta: num(e.etaSquared),
        f: num(cohensF(e.etaSquared)),
      }),
    ],
    headline: { value: e.etaSquared, family: "eta" },
  };
}

function convertedD(from: "r" | "d" | "or", value: number): number | null {
  if (from === "r") {
    return Math.abs(value) < 1 ? rToD(value) : null;
  }
  if (from === "or") {
    return value > 0 ? oddsRatioToD(value) : null;
  }
  return value;
}

export function convertView(
  fields: Fields,
  from: "r" | "d" | "or",
  strings: Strings,
  num: Num
): EffectView | null {
  const value = parseNumberInput(fields.value);
  if (value === null) {
    return null;
  }
  const d = convertedD(from, value);
  if (d === null) {
    return null;
  }
  const r = from === "r" ? value : dToR(d);
  const oddsRatio = from === "or" ? value : dToOddsRatio(d);
  const lines = {
    rToD: t(strings.rToDLine, { r: num(r), d: num(d) }),
    dToR: t(strings.dToRLine, { d: num(d), r: num(r) }),
    orToD: t(strings.orToDLine, { or: num(oddsRatio), d: num(d) }),
    dToOr: t(strings.dToOrLine, { d: num(d), or: num(oddsRatio) }),
  };
  // Each conversion runs through d, so the line into d comes first.
  const order: Record<typeof from, string[]> = {
    r: [lines.rToD, lines.dToOr],
    d: [lines.dToR, lines.dToOr],
    or: [lines.orToD, lines.dToR],
  };
  const working = [...order[from], strings.conversionNote];
  return {
    cards: [
      { label: strings.cohensD, value: num(d), emphasis: from !== "d" },
      { label: strings.correlationR, value: num(r), emphasis: from !== "r" },
      {
        label: strings.oddsRatio,
        value: num(oddsRatio),
        emphasis: from !== "or",
      },
    ],
    working,
    headline: { value: d, family: "d" },
  };
}

const SCALE_KEYS: Record<
  EffectFamily,
  "scaleD" | "scaleR" | "scaleEta" | "scaleF"
> = {
  d: "scaleD",
  r: "scaleR",
  eta: "scaleEta",
  f: "scaleF",
};

export function benchmarkSentence(
  headline: EffectView["headline"],
  strings: Strings,
  num: Num
): string | null {
  if (!(headline && Number.isFinite(headline.value))) {
    return null;
  }
  const magnitude = cohenMagnitude(headline.value, headline.family);
  return t(strings.benchmarkLine, {
    value: num(headline.value),
    label: strings[magnitude],
    scale: strings[SCALE_KEYS[headline.family]],
  });
}
